import {
    ISP_DETAILS,
    LICENCE_TYPE,
    SUPPORTING_DOCUMENTS,
    TARGET_MARKET_USERS
} from 'common/application.const/Licence/MyLicence/ISPLicence';
import {
    LICENCE_DETAILS,
    SUPPORTING_DOCUMENTS as CANCEL_SUPPORTING_DOCUMENTS
} from 'common/application.const/Licence/MyApplication/cancelLicence';

import { LICENCE_DETAILS as RENEW_LICENCE_DETAILS } from 'common/application.const/Licence/MyApplication/renewLicence';
import { attachmentPayloadSetting, attachmentRetrieve, requestLicenceAttachment } from 'features/applicationPage/helpers';

import { formatDateTo_ddMMyyyy } from 'utils/application.helpers';

export const formatISP = async (licenceDetails: any, caseTypeID: string, application?: string, transactionType?: any) => {
    let licence;
    let newDocs;
    let newPayload;
    let docsLic;

    newDocs = [];
    for (const key in licenceDetails.LICSupportingDoc) {
        if (licenceDetails.LICSupportingDoc.hasOwnProperty(key)) {
            const doc = await requestLicenceAttachment(licenceDetails.LICSupportingDoc[key], caseTypeID);
            if (doc.ID) {
                const attachment = {
                    ID: doc.ID,
                    fileName: doc.fileName,
                    mimeType: doc.mimeType,
                    category: doc.category
                };
                newDocs.push(attachment);
            }
        }
    }

    newPayload = attachmentPayloadSetting(newDocs);
    docsLic = attachmentRetrieve(newPayload);

    licence = {
        id: 'internet-service-provider-licence',
        formID: 'internet-service-provider-licence',
        hasError: true,
        title: application === 'IMDA-IRIS-Work-LICApplication-BCG' ? 'Application Details' : 'Licence Details',
        values: {
            [`${LICENCE_TYPE}-start-date`]: licenceDetails?.LicenceCategoryDetails?.ValidityFromDate ?? '-',
            [`${LICENCE_TYPE}-end-date`]: licenceDetails?.LicenceCategoryDetails?.ValidityToDate ?? '-',
            [`${LICENCE_TYPE}-do-you-have-sbo-fbo`]:
                licenceDetails?.licenceApplicationInformation?.doYouHaveASboOrFboLicenceFromImda ?? '-',
            [`${LICENCE_TYPE}-licence-type`]: licenceDetails?.licenceApplicationInformation?.licenceCategory ?? '-',

            [`${ISP_DETAILS}-type-of-services`]:
                licenceDetails?.internetServiceDetails?.typeOfServices
                    ?.filter((item: any) => item.pySelected === 'true')
                    ?.map((item: any) => item.mixedValue)
                    ?.join('\n') ?? '-',
            [`${ISP_DETAILS}-others`]: licenceDetails?.internetServiceDetails?.IfOtherServicesPleaseSpecify ?? '-',
            [`${TARGET_MARKET_USERS}-target-type`]:
                licenceDetails?.internetServiceDetails?.targetMarketUsers
                    ?.filter((item: any) => item.pySelected === 'true')
                    ?.map((item: any) => item.mixedValue)
                    ?.join('\n') ?? '-',
            [`${TARGET_MARKET_USERS}-others`]: licenceDetails?.internetServiceDetails?.IfOtherTargetMarketUsersPleaseSpecify ?? '-',
            [`${TARGET_MARKET_USERS}-service-start-date`]: licenceDetails?.internetServiceDetails?.serviceStartDate ?? '-',
            [`${TARGET_MARKET_USERS}-we-own-internet-access-points`]:
                licenceDetails?.internetServiceDetails?.weOwnInternetAccessPoints ?? '-',
            [`${TARGET_MARKET_USERS}-name-of-provider`]:
                licenceDetails?.internetServiceDetails?.nameOfProvider ?? '-',
            [`${SUPPORTING_DOCUMENTS}-supporting-documents`]: newDocs ? docsLic : undefined
        }
    };

    if (transactionType === 'Cancel') {
        licence = {
            id: 'cancel-licence-details',
            formID: 'cancel-licence-details',
            hasError: true,
            title: application === 'IMDA-IRIS-Work-LICApplication' ? 'Application Details' : 'Licence Details',
            values: {
                [`${LICENCE_DETAILS}-licence-no`]: licenceDetails?.cancellationInformation?.licenceNumber ?? '',

                [`${LICENCE_DETAILS}-cancellationReason`]: licenceDetails?.cancellationInformation?.cancellationReason ?? '',
                [`${LICENCE_DETAILS}-effectiveDateOfCancellation`]:
                    formatDateTo_ddMMyyyy(licenceDetails?.cancellationInformation?.effectiveDateOfCancellation) ?? '',

                [`${CANCEL_SUPPORTING_DOCUMENTS}-supporting-documents`]: newDocs ? docsLic : '-'
            }
        };
    }

    if (transactionType === 'Renew') {
        licence = {
            id: 'renew-licence-details',
            formID: 'renew-licence-details',
            hasError: true,
            title: 'Application Details',
            values: {
                [`${RENEW_LICENCE_DETAILS}-licensee-nric`]: licenceDetails?.licenceDetails?.licenseeNric ?? '',
                [`${RENEW_LICENCE_DETAILS}-registration-no-uen-other`]: licenceDetails?.licenceDetails?.uen ?? '',
                [`${RENEW_LICENCE_DETAILS}-licence-no`]: licenceDetails?.licenceDetails?.licenceNumber ?? '',

                [`${RENEW_LICENCE_DETAILS}-invoiceNo`]: licenceDetails?.licenceRenewal?.invoiceNumber ?? '',
                [`${RENEW_LICENCE_DETAILS}-renewalAmount`]: licenceDetails?.licenceRenewal?.renewalAmount ?? '',
                [`${RENEW_LICENCE_DETAILS}-giroIndicator`]: licenceDetails?.licenceRenewal?.giroIndicator ?? '',
                [`${RENEW_LICENCE_DETAILS}-dateOfExpiry`]: formatDateTo_ddMMyyyy(licenceDetails?.licenceRenewal?.dateOfExpiry) ?? '',
                [`${RENEW_LICENCE_DETAILS}-paymentDueDate`]: formatDateTo_ddMMyyyy(licenceDetails?.licenceRenewal?.paymentDueDate) ?? '',
                [`${RENEW_LICENCE_DETAILS}-renewalPeriod`]: licenceDetails?.licenceRenewal?.renewalPeriod ?? ''
            }
        };
    }

    if (transactionType === 'Amend') {
        const mapped = (
            licenceDetails?.iwantToAmend ||
            licenceDetails?.licenceDetails?.iwantToAmend ||
            licenceDetails?.iWantToUpdate ||
            licenceDetails?.licenceDetails?.iWantToUpdate
        )?.map((data: { [key: string]: string }) => {
            return {
                type: data.mixedValue
            };
        });

        licence = {
            id: 'amend-internet-service-provider-licence',
            formID: 'amend-internet-service-provider-licence',
            hasError: true,
            title: application === '"IMDA-IRIS-Work-LICApplication-BCG' ? 'Application Details' : 'Licence Details',
            values: {
                [`${LICENCE_TYPE}-amend-table`]: Array.isArray(mapped) && mapped.length > 0 ? [...mapped] : [],
                [`${LICENCE_TYPE}-start-date`]: licenceDetails?.LicenceCategoryDetails?.ValidityFromDate ?? '-',
                [`${LICENCE_TYPE}-end-date`]: licenceDetails?.LicenceCategoryDetails?.ValidityToDate ?? '-',
                [`${LICENCE_TYPE}-do-you-have-sbo-fbo`]:
                    licenceDetails?.licenceApplicationInformation?.doYouHaveASboOrFboLicenceFromImda ?? '-',
                [`${LICENCE_TYPE}-licence-type`]: licenceDetails?.licenceApplicationInformation?.licenceCategory ?? '-',

                [`${ISP_DETAILS}-type-of-services`]:
                    licenceDetails?.internetServiceDetails?.typeOfServices
                        ?.filter((item: any) => item.pySelected === 'true')
                        ?.map((item: any) => item.mixedValue)
                        ?.join('\n') ?? '-',
                [`${ISP_DETAILS}-others`]: licenceDetails?.internetServiceDetails?.IfOtherServicesPleaseSpecify ?? '-',
                [`${TARGET_MARKET_USERS}-target-type`]:
                    licenceDetails?.internetServiceDetails?.targetMarketUsers
                        ?.filter((item: any) => item.pySelected === 'true')
                        ?.map((item: any) => item.mixedValue)
                        ?.join('\n') ?? '-',
                [`${TARGET_MARKET_USERS}-others`]: licenceDetails?.internetServiceDetails?.IfOtherTargetMarketUsersPleaseSpecify ?? '-',
                [`${TARGET_MARKET_USERS}-service-start-date`]: licenceDetails?.internetServiceDetails?.serviceStartDate ?? '-',
                [`${TARGET_MARKET_USERS}-we-own-internet-access-points`]:
                    licenceDetails?.internetServiceDetails?.weOwnInternetAccessPoints ?? '-',
                [`${TARGET_MARKET_USERS}-name-of-provider`]:
                licenceDetails?.internetServiceDetails?.nameOfProvider ?? '-',
                [`${SUPPORTING_DOCUMENTS}-supporting-documents`]: newDocs ? docsLic : undefined
            }
        };
    }

    return licence;
};

const ispPayload = {
    formatISP
};

export default ispPayload;
